/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { getSystemVendorInvoices } from './functions/get_system_vendor_invoices.function';
import { getSystemVendorInvoicesByPo } from './functions/get_system_vendor_invoices_by_po.function';
import { getSystemPoAuthorizedRecordByPo } from '../SystemPurchaseOrderManagement/functions/get_system_po_authorized_record_by_po.function';
import { buildPoInvoiceSummary } from '../ApprovalPersonsInvoiceManagement/utils/po_invoice_summary.util';
import {
  getDocumentBlob,
  revokeBlobUrl,
} from '../ApprovalPersonsInvoiceManagement/functions/get_document_blob.function';
import VendorInvoiceHeader from '../VendorInvoiceManagement/components/vendor_invoice_header.component';
import InvoiceDetailsCard from '../ApprovalPersonsInvoiceManagement/components/invoice_details_card.component';
import DocumentsList from '../ApprovalPersonsInvoiceManagement/components/documents_list.component';
import InvoicePreview from '../ApprovalPersonsInvoiceManagement/components/invoice_preview.component';
import DocumentPreview from '../ApprovalPersonsInvoiceManagement/components/document_preview.component';

const SystemVendorInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const rawParam = params.invoiceNumber || params['*'] || '';
  let decodedInvoiceNumber = rawParam;
  try {
    decodedInvoiceNumber = decodeURIComponent(rawParam);
  } catch (e) {
    decodedInvoiceNumber = rawParam;
  }
  const passedInvoiceData = location.state?.invoiceData;

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('System Invoice Management Vendor')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [invoiceData, setInvoiceData] = useState(null);
  const [serviceItemsData, setServiceItemsData] = useState([]);
  const [sitesData, setSitesData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [rawInvoiceSource, setRawInvoiceSource] = useState(passedInvoiceData || null);
  const [poInvoicesForSummary, setPoInvoicesForSummary] = useState([]);
  const [poDetails, setPoDetails] = useState(null);

  const currentInvoiceNumber = decodedInvoiceNumber;

  const totalInvoiceAmount = useMemo(() => {
    const parseNumber = (v) => {
      if (v == null) {
        return 0;
      }
      const n = Number(String(v).replace(/,/g, '').trim());
      return Number.isFinite(n) ? n : 0;
    };
    return (serviceItemsData || []).reduce((sum, it) => {
      const qty = parseNumber(it?.invoiced_qty ?? it?.invoicedQty);
      const unit = parseNumber(it?.unit_price ?? it?.unitPrice);
      return sum + qty * unit;
    }, 0);
  }, [serviceItemsData]);

  const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return String(v ?? '0');
    }
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const siteChipColors = [
    { backgroundColor: '#E3F2FD', color: '#0D47A1', borderColor: '#BBDEFB' },
    { backgroundColor: '#E8F5E9', color: '#1B5E20', borderColor: '#C8E6C9' },
    { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#FFE0B2' },
    { backgroundColor: '#F3E5F5', color: '#4A148C', borderColor: '#E1BEE7' },
    { backgroundColor: '#E0F2F1', color: '#004D40', borderColor: '#B2DFDB' },
    { backgroundColor: '#FCE4EC', color: '#880E4F', borderColor: '#F8BBD0' },
  ];

  const mapInvoiceData = useCallback(
    (data) => {
      const formatDate = (dateString) => {
        if (!dateString) {
          return '';
        }
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        } catch (e) {
          return dateString;
        }
      };

      const poSummary = buildPoInvoiceSummary({
        rawInvoice: data,
        poInvoicesList: poInvoicesForSummary,
        poDetails,
      });

      const mappedInvoiceData = {
        invoiceNumber: data.invoice_number || data.invoiceNumber || '',
        title: data.invoice_number || data.invoiceNumber || '',
        invoiceNo: data.invoice_number || data.invoiceNumber || '',
        category: data.category || '',
        vendorName: data.vendor || '',
        invoiceDate: formatDate(data.created_at || data.invoice_date),
        region: data.region || '',
        poNumber: data.po_number || data.poNumber || '',
        poStatus: 'Active',
        approvalStatus: data.status
          ? data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase()
          : '',
        status: data.status || '',
        vendorRemarks: data.vendor_remarks || data.vendorRemarks || '',
        invoiceType: data.invoice_type || data.invoiceType || '',
        poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
        alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
        poInvoicesCount: poSummary.poInvoicesCount,
        reasonOfRejection: poSummary.reasonOfRejection,
      };

      const mappedServiceItemsData = Array.isArray(data.service_items_data)
        ? data.service_items_data
        : [];
      const mappedSites = Array.isArray(data.sites)
        ? data.sites.map((site) => String(site || '').trim()).filter(Boolean)
        : [];

      const mappedDocuments = [];
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';

      // PO file
      if (data.po_file_path) {
        const fileName = data.po_file_path.split('/').pop() || 'PO File.pdf';
        mappedDocuments.push({
          id: 'po-file',
          name: 'PO File',
          fileName,
          filePath: data.po_file_path,
          url: `${apiBaseUrl}/${data.po_file_path}`,
          type: 'application/pdf',
        });
      }

      // Other documents (same shape as vendor module)
      if (data.other_documents_paths && Array.isArray(data.other_documents_paths)) {
        data.other_documents_paths.forEach((docPath, index) => {
          const fileName = docPath.split('/').pop() || `Document ${index + 1}`;
          const lowerPath = docPath.toLowerCase();
          const lowerFileName = fileName.toLowerCase();
          let fileType = 'application/octet-stream';
          if (lowerPath.endsWith('.pdf') || lowerFileName.endsWith('.pdf')) {
            fileType = 'application/pdf';
          } else if (lowerPath.endsWith('.xlsx') || lowerFileName.endsWith('.xlsx')) {
            fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          } else if (lowerPath.endsWith('.xls') || lowerFileName.endsWith('.xls')) {
            fileType = 'application/vnd.ms-excel';
          } else if (lowerPath.endsWith('.csv') || lowerFileName.endsWith('.csv')) {
            fileType = 'text/csv';
          } else if (lowerPath.endsWith('.msg') || lowerFileName.endsWith('.msg')) {
            fileType = 'application/vnd.ms-outlook';
          }

          mappedDocuments.push({
            id: `other-doc-${index}`,
            name: fileName,
            fileName,
            filePath: docPath,
            url: `${apiBaseUrl}/systemVendorInvoiceManagement/invoice/document/${docPath}`,
            type: fileType,
          });
        });
      }

      return {
        mappedInvoiceData,
        mappedServiceItemsData,
        mappedSites,
        mappedDocuments,
        invoicePreviewData: data,
      };
    },
    [poInvoicesForSummary, poDetails]
  );

  useEffect(() => {
    const poNum = (
      rawInvoiceSource?.po_number ||
      rawInvoiceSource?.poNumber ||
      passedInvoiceData?.po_number ||
      passedInvoiceData?.poNumber ||
      ''
    ).trim();
    if (!poNum) {
      setPoInvoicesForSummary([]);
      setPoDetails(null);
      return undefined;
    }

    let cancelled = false;
    const loadPoSummaryData = async () => {
      try {
        const [poInvoicesRes, poRecordRes] = await Promise.all([
          getSystemVendorInvoicesByPo(poNum),
          getSystemPoAuthorizedRecordByPo(poNum),
        ]);

        const payload = poInvoicesRes?.response ?? poInvoicesRes?.data ?? poInvoicesRes;
        const invoices = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.invoices)
            ? payload.invoices
            : [];

        if (!cancelled) {
          setPoInvoicesForSummary(invoices);
          setPoDetails(poRecordRes?.success ? poRecordRes.data : null);
        }
      } catch (err) {
        if (!cancelled) {
          setPoInvoicesForSummary([]);
          setPoDetails(null);
        }
      }
    };

    loadPoSummaryData();
    return () => {
      cancelled = true;
    };
  }, [
    rawInvoiceSource?.po_number,
    rawInvoiceSource?.poNumber,
    passedInvoiceData?.po_number,
    passedInvoiceData?.poNumber,
  ]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const base = passedInvoiceData ? passedInvoiceData : null;
        let data = base;
        if (!data) {
          const res = await getSystemVendorInvoices();
          const list = Array.isArray(res?.response)
            ? res.response
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];
          data = list.find(
            (inv) => (inv.invoice_number || inv.invoiceNumber) === currentInvoiceNumber
          );
        }

        if (!data) {
          throw new Error('Invoice not found');
        }

        if (isMounted) {
          setRawInvoiceSource(data);
        }

        const mapped = mapInvoiceData(data);
        if (isMounted) {
          setInvoiceData(mapped.mappedInvoiceData);
          setServiceItemsData(mapped.mappedServiceItemsData);
          setSitesData(mapped.mappedSites);
          setDocuments(mapped.mappedDocuments);
          setInvoicePreviewData(mapped.invoicePreviewData);
          if (mapped.mappedDocuments.length > 0) {
            setSelectedDocumentIndex(0);
            setSelectedDocument(mapped.mappedDocuments[0]);
            setShowDocumentPreview(true);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || 'Failed to load invoice details');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [passedInvoiceData, currentInvoiceNumber]);

  useEffect(() => {
    if (!rawInvoiceSource) {
      return;
    }
    const mapped = mapInvoiceData(rawInvoiceSource);
    setInvoiceData(mapped.mappedInvoiceData);
    setServiceItemsData(mapped.mappedServiceItemsData);
    setSitesData(mapped.mappedSites);
    setDocuments(mapped.mappedDocuments);
    setInvoicePreviewData(mapped.invoicePreviewData);
  }, [rawInvoiceSource, mapInvoiceData, poInvoicesForSummary, poDetails]);

  useEffect(() => {
    return () => {
      if (documentBlobUrl) {
        revokeBlobUrl(documentBlobUrl);
      }
    };
  }, [documentBlobUrl]);

  const handleBack = () => {
    navigate('/systemInvoiceManagementVendor');
  };

  const handleDocumentClick = async (index, document) => {
    const previousBlobUrl = documentBlobUrl;
    if (previousBlobUrl) {
      revokeBlobUrl(previousBlobUrl);
    }

    setDocumentBlobUrl(null);
    setLoadingDocument(false);
    setSelectedDocumentIndex(index);
    setSelectedDocument(document);
    setShowDocumentPreview(true);
  };

  if (loading) {
    return (
      <DashboardLayout padding={true}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout padding={true}>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout padding={true}>
      <Box sx={{ p: 2 }}>
        <VendorInvoiceHeader onBack={handleBack} invoiceNumber={invoiceData?.title || ''} />
        <Grid container spacing={3}>
          {/* Left Column: Invoice details + breakdown below + documents list */}
          <Grid item xs={12} md={6}>
            <InvoiceDetailsCard invoiceData={invoiceData} hideApproverFields hideCategory />
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '16px',
                border: '1px solid #EAECF0',
                background:
                  'linear-gradient(135deg, rgba(189, 29, 35, 0.06) 0%, rgba(25, 118, 210, 0.05) 100%)',
                boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.06)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontFamily: 'Inter',
                      fontWeight: 800,
                      color: '#101828',
                    }}
                  >
                    Sites
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#667085', mt: 0.25 }}>
                    Sites included in this invoice
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${sitesData.length} site${sitesData.length === 1 ? '' : 's'}`}
                  sx={{
                    backgroundColor: '#BD1D23',
                    color: '#fff',
                    fontWeight: 800,
                    borderRadius: '999px',
                  }}
                />
              </Box>

              {sitesData.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {sitesData.map((site, index) => {
                    const chipColor = siteChipColors[index % siteChipColors.length];
                    return (
                      <Chip
                        key={`${site}-${index}`}
                        size="small"
                        label={site}
                        sx={{
                          ...chipColor,
                          border: `1px solid ${chipColor.borderColor}`,
                          borderRadius: '10px',
                          fontWeight: 800,
                          letterSpacing: 0.2,
                          boxShadow: '0px 2px 6px rgba(16, 24, 40, 0.08)',
                        }}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    border: '1px dashed #D0D5DD',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: '#667085' }}>
                    No sites found for this invoice.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  mb: 2,
                  color: '#192126',
                }}
              >
                Service Items
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #E8E9E9',
                  borderRadius: '8px',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#344054', width: 56 }}>
                        Sr
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Code</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>PO Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Claimed Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>
                        Remaining Qty
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Invoiced Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(serviceItemsData || []).map((it, idx) => {
                      const qty = Number(it?.invoiced_qty ?? 0) || 0;
                      const unit =
                        Number(
                          String(it?.unit_price ?? 0)
                            .replace(/,/g, '')
                            .trim()
                        ) || 0;
                      const amount = qty * unit;
                      return (
                        <TableRow key={`${it?.item_code || 'item'}-${idx}`}>
                          <TableCell sx={{ color: '#667085' }}>{idx + 1}</TableCell>
                          <TableCell>{it?.item_code ?? '—'}</TableCell>
                          <TableCell>{it?.item_type ?? '—'}</TableCell>
                          <TableCell>{it?.po_qty ?? 0}</TableCell>
                          <TableCell>{it?.claimed_qty ?? 0}</TableCell>
                          <TableCell>{it?.remaining_qty ?? 0}</TableCell>
                          <TableCell>{it?.invoiced_qty ?? 0}</TableCell>
                          <TableCell>{it?.unit_price ?? '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#101828' }}>
                            {formatMoney(amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!serviceItemsData || serviceItemsData.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ color: '#667085' }}>
                          No service items found.
                        </TableCell>
                      </TableRow>
                    )}
                    {serviceItemsData && serviceItemsData.length > 0 && (
                      <TableRow
                        sx={{
                          background:
                            'linear-gradient(90deg, rgba(189, 29, 35, 0.10) 0%, rgba(16, 185, 129, 0.10) 100%)',
                        }}
                      >
                        <TableCell colSpan={8} sx={{ fontWeight: 800, color: '#101828' }}>
                          Total Invoice Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, color: '#101828' }}>
                          {formatMoney(totalInvoiceAmount)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box sx={{ mt: 2 }} />
            <DocumentsList
              documents={documents}
              selectedIndex={selectedDocumentIndex}
              onDocumentClick={handleDocumentClick}
            />
          </Grid>

          {/* Right Column: Document preview panel */}
          <Grid item xs={12} md={6}>
            {showDocumentPreview && selectedDocument ? (
              <DocumentPreview
                key={`sys-doc-preview-${selectedDocumentIndex}-${selectedDocument?.id || selectedDocumentIndex}-${documentBlobUrl || selectedDocument?.url || ''}`}
                documentUrl={(() => {
                  const isExcelOrCSVOrMsg =
                    selectedDocument.type ===
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    selectedDocument.type === 'application/vnd.ms-excel' ||
                    selectedDocument.type === 'text/csv' ||
                    selectedDocument.type === 'application/vnd.ms-outlook' ||
                    /\.(xlsx|xls|csv|msg)$/i.test(
                      selectedDocument.fileName || selectedDocument.name || ''
                    );

                  let baseUrl =
                    documentBlobUrl ||
                    selectedDocument.blobUrl ||
                    selectedDocument.url ||
                    selectedDocument.documentUrl ||
                    selectedDocument.fileUrl ||
                    null;

                  if (isExcelOrCSVOrMsg && baseUrl && !baseUrl.includes('blob:')) {
                    const separator = baseUrl.includes('?') ? '&' : '?';
                    baseUrl = `${baseUrl}${separator}format=pdf`;
                  }

                  return baseUrl;
                })()}
                downloadUrl={(() => {
                  return (
                    documentBlobUrl ||
                    selectedDocument.blobUrl ||
                    selectedDocument.url ||
                    selectedDocument.documentUrl ||
                    selectedDocument.fileUrl ||
                    null
                  );
                })()}
                documentName={
                  selectedDocument.name ||
                  selectedDocument.fileName ||
                  selectedDocument.documentName ||
                  'Document'
                }
                documentType={(() => {
                  const isExcelOrCSVOrMsg =
                    selectedDocument.type ===
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    selectedDocument.type === 'application/vnd.ms-excel' ||
                    selectedDocument.type === 'text/csv' ||
                    selectedDocument.type === 'application/vnd.ms-outlook' ||
                    /\.(xlsx|xls|csv|msg)$/i.test(
                      selectedDocument.fileName || selectedDocument.name || ''
                    );
                  if (isExcelOrCSVOrMsg) {
                    return 'application/pdf';
                  }
                  return selectedDocument.type || 'application/pdf';
                })()}
                loading={loadingDocument}
              />
            ) : (
              invoicePreviewData && <InvoicePreview invoicePreviewData={invoicePreviewData} />
            )}
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};
export default SystemVendorInvoiceDetails;
